import React from 'react';
import type { StatusOption } from '../types';

interface FilterOption {
  value: string;
  label: string;
}

const timelineOptions: FilterOption[] = [
  { value: 'all', label: 'Alle fases' },
  { value: 'early', label: 'Planningfase' },
  { value: 'preparation', label: 'Voorbereidingsfase' },
  { value: 'implementation', label: 'Implementatiefase' },
  { value: 'closed', label: 'Afgesloten' },
];

const sortOptions: FilterOption[] = [
  { value: 'key-asc', label: 'Ticket nummer (A-Z)' },
  { value: 'key-desc', label: 'Ticket nummer (Z-A)' },
  { value: 'created-desc', label: 'Aanmaakdatum (nieuwste eerst)' },
  { value: 'created-asc', label: 'Aanmaakdatum (oudste eerst)' },
  { value: 'votes-desc', label: 'Aantal stemmen (hoog-laag)' },
  { value: 'votes-asc', label: 'Aantal stemmen (laag-hoog)' },
  { value: 'status-asc', label: 'Status (A-Z)' },
  { value: 'status-desc', label: 'Status (Z-A)' },
  { value: 'timeline-asc', label: 'Tijdlijn (vroeg-laat)' },
  { value: 'timeline-desc', label: 'Tijdlijn (laat-vroeg)' },
];

interface FilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  sortBy: string;
  onSortChange: (value: string) => void;
  activeFilter: string;
  statusFilter: string;
  statusOptions: StatusOption[];
  onStatusFilterChange: (value: string) => void;
  timelineFilter: string;
  onTimelineFilterChange: (value: string) => void;
  myVoteFilter: string;
  onMyVoteFilterChange: (value: string) => void;
  minVotes: string;
  onMinVotesChange: (value: string) => void;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  searchTerm,
  onSearchChange,
  sortBy,
  onSortChange,
  activeFilter,
  statusFilter,
  statusOptions,
  onStatusFilterChange,
  timelineFilter,
  onTimelineFilterChange,
  myVoteFilter,
  onMyVoteFilterChange,
  minVotes,
  onMinVotesChange,
  showAdvanced,
  onToggleAdvanced,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex gap-4 flex-col sm:flex-row">
        <div className="relative flex-1">
          <i className="fa-light fa-magnifying-glass absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            placeholder="Zoek op ticketnummer, samenvatting of beschrijving..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-11 pr-10 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-neutral-900 placeholder-neutral-400"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
            >
              <i className="fa-light fa-xmark" />
            </button>
          )}
        </div>
        <div className="sm:w-[300px]">
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-neutral-900 bg-white"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <button
          onClick={onToggleAdvanced}
          className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center gap-2"
        >
          <i
            className={`fa-light ${showAdvanced ? 'fa-chevron-down' : 'fa-chevron-right'}`}
          />
          Geavanceerde filters
        </button>
      </div>
      {showAdvanced && (
        <div className="border border-neutral-300 rounded-lg p-4 bg-neutral-50 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm bg-white"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {activeFilter === 'all' && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Tijdlijn fase
                </label>
                <select
                  value={timelineFilter}
                  onChange={(e) => onTimelineFilterChange(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm bg-white"
                >
                  {timelineOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Mijn stem
              </label>
              <select
                value={myVoteFilter}
                onChange={(e) => onMyVoteFilterChange(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm bg-white"
              >
                <option value="all">Alle tickets</option>
                <option value="voted">Ik heb gestemd</option>
                <option value="not-voted">Ik heb niet gestemd</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Minimum stemmen
              </label>
              <input
                type="number"
                min="0"
                value={minVotes}
                onChange={(e) => onMinVotesChange(e.target.value)}
                placeholder="0"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
